import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Paintbrush, Check, AlertTriangle, User, Mail, Phone, Lock, Save, Wrench, Camera, MapPin, X, Plus, Sparkles, Star, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { COUNTRIES, getRegionsByCountry, RADIUS_OPTIONS } from '../constants/locations';

import { API_URL } from '../utils/api';

const SellerSettingsPage = () => {
  const navigate = useNavigate();
  const { user, api, loading: authLoading, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [settings, setSettings] = useState({
    accepts_custom_orders: false,
    custom_order_description: ''
  });

  // Profile form state
  const [profile, setProfile] = useState({
    name: '',
    phone: '',
    location: '',
    profile_photo: '',
    bio: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  // Trade services state
  const [tradeServices, setTradeServices] = useState([]);
  const [tradeSettings, setTradeSettings] = useState({
    is_tradie: false,
    trade_services: [],
    trade_description: '',
    trade_service_country: 'AU',
    trade_service_state: '',
    trade_service_suburb: '',
    trade_service_postcode: '',
    trade_service_radius: 50,
    trade_photos: []
  });
  const [savingTrades, setSavingTrades] = useState(false);
  const [uploadingTradePhoto, setUploadingTradePhoto] = useState(false);
  const tradePhotoInputRef = useRef(null);

  // Get regions based on selected country
  const tradeRegions = getRegionsByCountry(tradeSettings.trade_service_country);

  // Email form state
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    password: ''
  });
  const [savingEmail, setSavingEmail] = useState(false);

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [savingPassword, setSavingPassword] = useState(false);

  // Advertising popup state
  const [showAdPopup, setShowAdPopup] = useState(false);
  const [subscribingAd, setSubscribingAd] = useState(false);
  
  // Get Featured prompt modal state
  const [showGetFeaturedPrompt, setShowGetFeaturedPrompt] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!user.is_seller) {
      navigate('/subscription');
      return;
    }
    
    // Check URL params for ad subscription result
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('ad_success') === 'true') {
      toast.success('Profile advertising activated!');
      window.history.replaceState({}, '', '/seller/settings');
      refreshUser();
    }
    if (urlParams.get('ad_cancel') === 'true') {
      toast.info('Advertising subscription cancelled');
      window.history.replaceState({}, '', '/seller/settings');
    }

    // Load current settings
    setSettings({
      accepts_custom_orders: user.accepts_custom_orders || false,
      custom_order_description: user.custom_order_description || ''
    });

    // Load profile data
    setProfile({
      name: user.name || '',
      phone: user.phone || '',
      location: user.location || '',
      profile_photo: user.profile_photo || '',
      bio: user.bio || ''
    });

    // Load trade settings
    setTradeSettings({
      is_tradie: user.is_tradie || false,
      trade_services: user.trade_services || [],
      trade_description: user.trade_description || '',
      trade_service_country: user.trade_service_country || user.country || 'AU',
      trade_service_state: user.trade_service_state || '',
      trade_service_suburb: user.trade_service_suburb || '',
      trade_service_postcode: user.trade_service_postcode || '',
      trade_service_radius: user.trade_service_radius || 50,
      trade_photos: user.trade_photos || []
    });

    // Fetch trade services
    fetchTradeServices();
  }, [user, authLoading, navigate]);

  const fetchTradeServices = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/trades/services`);
      setTradeServices(res.data.services);
    } catch (err) {
      console.error('Failed to fetch trade services', err);
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api().post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      setProfile({ ...profile, profile_photo: res.data.url });
      toast.success('Photo uploaded!');
    } catch (err) {
      console.error('Failed to upload photo', err);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleTradePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (tradeSettings.trade_photos.length + files.length > 20) {
      toast.error('Maximum 20 portfolio photos allowed');
      return;
    }

    setUploadingTradePhoto(true);
    const newPhotos = [];

    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api().post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000
        });
        newPhotos.push(res.data.url);
      } catch (err) {
        console.error('Failed to upload trade photo', err);
      }
    }

    if (newPhotos.length > 0) {
      setTradeSettings({
        ...tradeSettings,
        trade_photos: [...tradeSettings.trade_photos, ...newPhotos]
      });
      toast.success(`${newPhotos.length} photo(s) uploaded!`);
    }
    setUploadingTradePhoto(false);
  };

  const removeTradePhoto = (index) => {
    const newPhotos = [...tradeSettings.trade_photos];
    newPhotos.splice(index, 1);
    setTradeSettings({ ...tradeSettings, trade_photos: newPhotos });
  };

  const toggleTradeService = (serviceId) => {
    const current = tradeSettings.trade_services;
    if (current.includes(serviceId)) {
      setTradeSettings({
        ...tradeSettings,
        trade_services: current.filter(s => s !== serviceId)
      });
    } else if (current.length < 10) {
      setTradeSettings({
        ...tradeSettings,
        trade_services: [...current, serviceId]
      });
    } else {
      toast.error('Maximum 10 services allowed');
    }
  };

  const handleTradeSettingsSave = async () => {
    setSavingTrades(true);
    try {
      await api().put('/profile/trades', tradeSettings);
      await refreshUser();
      toast.success('Trade settings saved!');
      
      // Show Get Featured prompt if user just enabled tradie services
      // BUT not for VIP users who get it free
      const isVIP = user.promo_code_used === 'CMFFL26EF' || user.tradie_subscription_status === 'free_for_life';
      if (tradeSettings.is_tradie && !isVIP) {
        setShowGetFeaturedPrompt(true);
      }
    } catch (err) {
      console.error('Failed to save trade settings', err);
      toast.error('Failed to save trade settings');
    } finally {
      setSavingTrades(false);
    }
  };

  const handleSubscribeAdvertising = async () => {
    setSubscribingAd(true);
    try {
      const res = await api().post('/trades/advertising/subscribe');
      
      // If user has CMFFL26EF, they get free advertising without checkout
      if (res.data.free_for_life) {
        await refreshUser();
        toast.success(res.data.message || 'Your VIP code gives you free advertising!');
        setSubscribingAd(false);
        return;
      }
      
      // Otherwise redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error('Failed to subscribe to advertising', err);
      toast.error(err.response?.data?.detail || 'Failed to start subscription');
      setSubscribingAd(false);
    }
  };

  const handleCancelAdvertising = async () => {
    if (!window.confirm('Are you sure you want to cancel your profile advertising? Your profile will no longer be featured.')) {
      return;
    }
    
    try {
      await api().post('/trades/advertising/cancel');
      await refreshUser();
      toast.success('Advertising subscription cancelled');
    } catch (err) {
      console.error('Failed to cancel advertising', err);
      toast.error('Failed to cancel subscription');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api().put('/seller/custom-orders', settings);
      await refreshUser();
      toast.success('Settings saved!');
    } catch (err) {
      console.error('Failed to save settings', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    
    if (!profile.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSavingProfile(true);
    try {
      await api().put('/profile', {
        name: profile.name,
        phone: profile.phone,
        location: profile.location,
        profile_photo: profile.profile_photo,
        bio: profile.bio
      });
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (err) {
      console.error('Failed to update profile', err);
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleEmailSave = async (e) => {
    e.preventDefault();
    
    if (!emailForm.newEmail || !emailForm.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setSavingEmail(true);
    try {
      await api().put('/profile/email', {
        new_email: emailForm.newEmail,
        password: emailForm.password
      });
      await refreshUser();
      setEmailForm({ newEmail: '', password: '' });
      toast.success('Email updated successfully');
    } catch (err) {
      console.error('Failed to update email', err);
      toast.error(err.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setSavingPassword(true);
    try {
      await api().put('/profile/password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success('Password changed successfully');
    } catch (err) {
      console.error('Failed to change password', err);
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleCancelMembership = async () => {
    setCancelling(true);
    try {
      await api().post('/subscription/cancel');
      await refreshUser();
      toast.success('Your membership has been cancelled');
      setShowCancelConfirm(false);
      navigate('/');
    } catch (err) {
      console.error('Failed to cancel membership', err);
      toast.error(err.response?.data?.detail || 'Failed to cancel membership');
    } finally {
      setCancelling(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-stone-300 border-t-teal-600 rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-teal-600 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Seller Settings
        </h1>
        <p className="text-stone-500 mb-8">
          Manage your profile, seller preferences, and account settings
        </p>

        {/* Profile Information */}
        <form onSubmit={handleProfileSave} className="bg-white border border-stone-200 rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <User className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="font-semibold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Profile Information
              </h2>
              <p className="text-sm text-stone-500">Update your personal details</p>
            </div>
          </div>

          {/* Profile Photo */}
          <div className="mb-6">
            <Label className="mb-2 block">Profile Photo</Label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-stone-100 overflow-hidden flex-shrink-0 relative">
                {profile.profile_photo ? (
                  <img
                    src={profile.profile_photo}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-stone-400">
                    {user?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
                {uploadingPhoto && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full" />
                  </div>
                )}
              </div>
              <div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePhotoUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="text-sm"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  {profile.profile_photo ? 'Change Photo' : 'Upload Photo'}
                </Button>
                {profile.profile_photo && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setProfile({ ...profile, profile_photo: '' })}
                    className="text-sm text-stone-500 ml-2"
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Your name"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                placeholder="04XX XXX XXX"
                className="mt-1"
              />
              <p className="text-xs text-stone-500 mt-1">Australian mobile number</p>
            </div>
            
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                type="text"
                value={profile.location}
                onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                placeholder="City, State"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="bio">About Me / Bio</Label>
              <Textarea
                id="bio"
                value={profile.bio}
                onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                placeholder="Tell buyers a bit about yourself, your experience, and what you make..."
                rows={4}
                className="mt-1"
                maxLength={500}
              />
              <p className="text-xs text-stone-500 mt-1">{profile.bio?.length || 0}/500 characters</p>
            </div>
          </div>

          <Button
            type="submit"
            disabled={savingProfile}
            className="w-full mt-6 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
          >
            {savingProfile ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Profile
              </>
            )}
          </Button>
        </form>

        {/* Email Address */}
        <form onSubmit={handleEmailSave} className="bg-white border border-stone-200 rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <Mail className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="font-semibold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Email Address
              </h2>
              <p className="text-sm text-stone-500">Current: {user.email}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="newEmail">New Email Address</Label>
              <Input
                id="newEmail"
                type="email"
                value={emailForm.newEmail}
                onChange={(e) => setEmailForm({ ...emailForm, newEmail: e.target.value })}
                placeholder="new@example.com"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="emailPassword">Current Password</Label>
              <Input
                id="emailPassword"
                type="password"
                value={emailForm.password}
                onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                placeholder="Enter your password to confirm"
                className="mt-1"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={savingEmail}
            className="w-full mt-6 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
          >
            {savingEmail ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Update Email
              </>
            )}
          </Button>
        </form>

        {/* Change Password */}
        <form onSubmit={handlePasswordSave} className="bg-white border border-stone-200 rounded-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <Lock className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="font-semibold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Change Password
              </h2>
              <p className="text-sm text-stone-500">Update your account password</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                placeholder="Enter current password"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="Enter new password"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                className="mt-1"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={savingPassword}
            className="w-full mt-6 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
          >
            {savingPassword ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                Change Password
              </>
            )}
          </Button>
        </form>

        {/* Custom Orders Section */}
        <div className="bg-white border border-stone-200 rounded-sm p-6 space-y-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
              <Paintbrush className="h-6 w-6 text-teal-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Made to Order
              </h2>
              <p className="text-sm text-stone-500 mt-1">
                Let buyers know you accept custom orders. They can request unique pieces tailored to their needs.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-stone-50 rounded-sm">
            <div>
              <Label htmlFor="custom-orders" className="text-stone-900 font-medium">
                Accept Custom Orders
              </Label>
              <p className="text-sm text-stone-500">
                Show a &quot;Request Custom Order&quot; button on your listings
              </p>
            </div>
            <Switch
              id="custom-orders"
              checked={settings.accepts_custom_orders}
              onCheckedChange={(checked) => setSettings({ ...settings, accepts_custom_orders: checked })}
              data-testid="custom-orders-toggle"
            />
          </div>

          {settings.accepts_custom_orders && (
            <div className="space-y-2">
              <Label htmlFor="description">
                What custom work do you offer? (optional)
              </Label>
              <Textarea
                id="description"
                value={settings.custom_order_description}
                onChange={(e) => setSettings({ ...settings, custom_order_description: e.target.value })}
                placeholder="E.g., I specialize in restoring mid-century furniture. I can also build custom pieces from reclaimed wood. Typical turnaround is 2-4 weeks."
                rows={4}
                className="mt-1"
                data-testid="custom-order-description"
              />
              <p className="text-xs text-stone-500">
                This will be shown to buyers when they request a custom order
              </p>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Save Custom Order Settings
              </>
            )}
          </Button>
        </div>

        {/* Trade Services Section */}
        <div className="bg-white border border-stone-200 rounded-sm p-6 space-y-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
              <Wrench className="h-6 w-6 text-teal-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Trade Services
              </h2>
              <p className="text-sm text-stone-500 mt-1">
                Offer your trade skills to buyers looking for professional services
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-stone-50 rounded-sm">
            <div>
              <Label htmlFor="is-tradie" className="text-stone-900 font-medium">
                I provide trade services
              </Label>
              <p className="text-sm text-stone-500">
                List your services and let buyers find you
              </p>
            </div>
            <Switch
              id="is-tradie"
              checked={tradeSettings.is_tradie}
              onCheckedChange={(checked) => setTradeSettings({ ...tradeSettings, is_tradie: checked })}
            />
          </div>

          {tradeSettings.is_tradie && (
            <>
              {/* Trade Services Selection */}
              <div>
                <Label className="mb-3 block">Services You Offer (select up to 10)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto p-2 border border-stone-200 rounded-sm">
                  {tradeServices.map((service) => (
                    <div
                      key={service.id}
                      onClick={() => toggleTradeService(service.id)}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        tradeSettings.trade_services.includes(service.id)
                          ? 'bg-teal-50 border border-teal-200'
                          : 'hover:bg-stone-50 border border-transparent'
                      }`}
                    >
                      <Checkbox
                        checked={tradeSettings.trade_services.includes(service.id)}
                        onCheckedChange={() => toggleTradeService(service.id)}
                      />
                      <span className="text-sm text-stone-700">{service.name}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-stone-500 mt-2">
                  {tradeSettings.trade_services.length}/10 services selected
                </p>
              </div>

              {/* Service Area - Location Targeting */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-start gap-2 mb-3">
                  <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-800 text-sm">Service Area</h4>
                    <p className="text-xs text-blue-600 mt-0.5">
                      Complete all fields to reach a more targeted clientele in your area
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      Country *
                    </Label>
                    <Select
                      value={tradeSettings.trade_service_country || 'AU'}
                      onValueChange={(value) => setTradeSettings({ 
                        ...tradeSettings, 
                        trade_service_country: value,
                        trade_service_state: '' // Reset state when country changes
                      })}
                    >
                      <SelectTrigger className="mt-1 bg-white">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{tradeSettings.trade_service_country === 'NZ' ? 'Region' : 'State'} *</Label>
                    <Select
                      value={tradeSettings.trade_service_state || undefined}
                      onValueChange={(value) => setTradeSettings({ ...tradeSettings, trade_service_state: value })}
                    >
                      <SelectTrigger className="mt-1 bg-white">
                        <SelectValue placeholder={`Select ${tradeSettings.trade_service_country === 'NZ' ? 'region' : 'state'}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {tradeRegions.map((region) => (
                          <SelectItem key={region.value} value={region.value}>
                            {region.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Suburb/City *</Label>
                    <Input
                      value={tradeSettings.trade_service_suburb}
                      onChange={(e) => setTradeSettings({ ...tradeSettings, trade_service_suburb: e.target.value })}
                      placeholder={tradeSettings.trade_service_country === 'NZ' ? "e.g., Ponsonby, Newmarket" : "e.g., Bondi, Richmond"}
                      className="mt-1 bg-white"
                    />
                  </div>
                  <div>
                    <Label>Postcode</Label>
                    <Input
                      value={tradeSettings.trade_service_postcode}
                      onChange={(e) => setTradeSettings({ ...tradeSettings, trade_service_postcode: e.target.value })}
                      placeholder={tradeSettings.trade_service_country === 'NZ' ? "e.g., 1021" : "e.g., 2026"}
                      className="mt-1 bg-white"
                      maxLength={4}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Service Radius</Label>
                    <Select
                      value={String(tradeSettings.trade_service_radius || 50)}
                      onValueChange={(value) => setTradeSettings({ ...tradeSettings, trade_service_radius: parseInt(value) })}
                    >
                      <SelectTrigger className="mt-1 bg-white">
                        <SelectValue placeholder="Select radius" />
                      </SelectTrigger>
                      <SelectContent>
                        {RADIUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-blue-500 mt-1">How far you&apos;re willing to travel</p>
                  </div>
                </div>
              </div>

              {/* Trade Description */}
              <div>
                <Label>About Your Services</Label>
                <Textarea
                  value={tradeSettings.trade_description}
                  onChange={(e) => setTradeSettings({ ...tradeSettings, trade_description: e.target.value })}
                  placeholder="Describe your services, experience, qualifications, and anything else buyers should know..."
                  rows={5}
                  className="mt-1"
                />
              </div>

              {/* Portfolio Photos */}
              <div>
                <Label className="mb-3 block">Portfolio Photos (up to 20)</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {tradeSettings.trade_photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded overflow-hidden group">
                      <img
                        src={photo}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeTradePhoto(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  ))}
                  {tradeSettings.trade_photos.length < 20 && (
                    <div>
                      <input
                        ref={tradePhotoInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleTradePhotoUpload}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => tradePhotoInputRef.current?.click()}
                        disabled={uploadingTradePhoto}
                        className="w-full aspect-square border-2 border-dashed border-stone-300 rounded flex flex-col items-center justify-center text-stone-400 hover:border-teal-400 hover:text-teal-500 transition-colors"
                      >
                        {uploadingTradePhoto ? (
                          <div className="animate-spin h-6 w-6 border-2 border-stone-400 border-t-transparent rounded-full" />
                        ) : (
                          <>
                            <Plus className="h-6 w-6" />
                            <span className="text-xs mt-1">Add</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-stone-500 mt-2">
                  Showcase your best work to attract potential clients
                </p>
              </div>
            </>
          )}

          {/* Advertising Subscription Section */}
          {tradeSettings.is_tradie && (
            <div className={`border rounded-sm p-5 mt-4 ${
              user.tradie_subscription_status === 'free_for_life' || user.promo_code_used === 'CMFFL26EF'
                ? 'bg-gradient-to-r from-purple-50 to-white border-purple-200'
                : 'bg-gradient-to-r from-teal-50 to-white border-teal-200'
            }`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  user.tradie_subscription_status === 'free_for_life' || user.promo_code_used === 'CMFFL26EF'
                    ? 'bg-purple-100'
                    : 'bg-teal-100'
                }`}>
                  <Wrench className={`h-5 w-5 ${
                    user.tradie_subscription_status === 'free_for_life' || user.promo_code_used === 'CMFFL26EF'
                      ? 'text-purple-700'
                      : 'text-teal-600'
                  }`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-stone-900">Profile Advertising</h3>
                  <p className="text-sm text-stone-600 mt-1">
                    Get your profile featured on our homepage and browse pages. Your profile will continuously rotate for all buyers to see!
                  </p>
                  
                  {/* VIP Free for Life Status */}
                  {(user.tradie_subscription_status === 'free_for_life' || user.promo_code_used === 'CMFFL26EF') ? (
                    <div className="mt-3">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                        🌟 VIP - Free for Life
                      </span>
                      <p className="text-sm text-purple-600 mt-2">
                        Your profile is featured at no cost - forever!
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Show trial status */}
                      {user.tradie_subscription_status === 'trial' && user.tradie_trial_ends_at && (
                        <p className="text-sm text-blue-600 mt-2 font-medium">
                          Free trial - ends {new Date(user.tradie_trial_ends_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                      
                      <p className="text-lg font-bold text-teal-600 mt-2">$7.99/month</p>
                      
                      {user.tradie_ad_active ? (
                        <div className="mt-3">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-3">
                            <Check className="h-4 w-4" />
                            Currently Advertising
                          </span>
                          <Button
                            variant="outline"
                            onClick={handleCancelAdvertising}
                            className="block text-stone-500 hover:text-teal-600"
                            size="sm"
                          >
                            Cancel Advertising
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={handleSubscribeAdvertising}
                          disabled={subscribingAd}
                          className="mt-3 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                        >
                          {subscribingAd ? 'Loading...' : 'Start Advertising'}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <Button
            onClick={handleTradeSettingsSave}
            disabled={savingTrades}
            className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white font-medium"
          >
            {savingTrades ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Save Trade Settings
              </>
            )}
          </Button>
        </div>

        {/* Advertising Popup Modal */}
        {showAdPopup && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Promote Your Profile! 🎉
              </h3>
              <p className="text-stone-600 mb-4">
                Would you like your profile advertised on our site? Your profile will be featured in a rotating carousel on the homepage and browse pages, helping you reach more customers!
              </p>
              <p className="text-lg font-bold text-teal-600 mb-4">Just $7.99/month after 30-day free trial</p>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowAdPopup(false)}
                  className="flex-1"
                >
                  No Thanks
                </Button>
                <Button
                  onClick={() => {
                    setShowAdPopup(false);
                    handleSubscribeAdvertising();
                  }}
                  className="flex-1 bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white"
                >
                  Yes, Advertise Me!
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Get Featured Prompt Modal - Shows after tradie saves their profile */}
        {showGetFeaturedPrompt && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Star className="h-8 w-8 text-amber-500" />
                </div>
                <h3 className="text-xl font-bold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  🌟 Want to Stand Out?
                </h3>
              </div>
              
              <p className="text-stone-600 mb-4 text-center">
                Get your profile <strong>featured on the homepage carousel</strong> and be seen by buyers in <strong>{user.trade_service_state || 'your area'}</strong> first!
              </p>
              
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 mb-4 border border-amber-200">
                <p className="text-sm font-medium text-amber-800 mb-2">Featured Promotion Pricing:</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-white rounded p-2 border border-amber-100">
                    <p className="font-bold text-stone-900">$20</p>
                    <p className="text-xs text-stone-500">7 days</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-amber-100">
                    <p className="font-bold text-stone-900">$35</p>
                    <p className="text-xs text-stone-500">14 days</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-amber-200 ring-2 ring-amber-300">
                    <p className="font-bold text-stone-900">$65</p>
                    <p className="text-xs text-stone-500">30 days</p>
                    <span className="text-[10px] text-amber-600">Best Value</span>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowGetFeaturedPrompt(false)}
                  className="flex-1"
                >
                  Maybe Later
                </Button>
                <Button
                  onClick={() => {
                    setShowGetFeaturedPrompt(false);
                    navigate('/get-featured');
                  }}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Sparkles className="h-4 w-4 mr-2" />
                  Get Featured
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Membership Section */}
        {(user.subscription_status === 'active' || user.subscription_status === 'trial') && (
          <div className="bg-white border border-stone-200 rounded-sm p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-stone-500" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Cancel Membership
                </h2>
                <p className="text-sm text-stone-500 mt-1">
                  {user.subscription_status === 'trial' 
                    ? 'Cancel your free trial. You can resubscribe at any time.'
                    : 'Cancel your subscription. You will retain access until the end of your billing period.'}
                </p>

                {!showCancelConfirm ? (
                  <Button
                    onClick={() => setShowCancelConfirm(true)}
                    variant="outline"
                    className="mt-4 text-teal-600 border-teal-200 hover:bg-teal-50"
                  >
                    Cancel Membership
                  </Button>
                ) : (
                  <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-sm">
                    <p className="text-sm text-teal-700 mb-4">
                      Are you sure you want to cancel? You will lose access to seller features.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleCancelMembership}
                        disabled={cancelling}
                        className="bg-teal-600 hover:bg-gradient-to-r from-sky-500 to-teal-500 text-white"
                      >
                        {cancelling ? (
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          'Yes, Cancel'
                        )}
                      </Button>
                      <Button
                        onClick={() => setShowCancelConfirm(false)}
                        variant="outline"
                      >
                        Keep Membership
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerSettingsPage;
